import { z } from 'zod';

import { AI_UNAVAILABLE_REASONS } from '../../ai/ai-unavailable/ai-unavailable';
import { type MediaKind } from '../../media/media-kind/media-kind';

/**
 * The member photo kinds the AI-generated-photo advisory looks at (P27b1).
 *
 * A subset of `MEDIA_KINDS`, declared here rather than inferred from ownership:
 * an `id_document` is a member upload too, and classifying a photograph of a
 * card as "generated" would be noise a reviewer has to learn to ignore.
 */
export const PHOTO_ADVISORY_KINDS = [
  'profile_photo',
  'selfie',
] as const satisfies readonly MediaKind[];

export const PhotoAdvisoryKindSchema = z.enum(PHOTO_ADVISORY_KINDS);

export type PhotoAdvisoryKind = (typeof PHOTO_ADVISORY_KINDS)[number];

const KIND_SET: ReadonlySet<string> = new Set<string>(PHOTO_ADVISORY_KINDS);

/** Narrows a media kind — what the finalize listener filters on. */
export function isPhotoAdvisoryKind(value: string): value is PhotoAdvisoryKind {
  return KIND_SET.has(value);
}

export const PHOTO_ADVISORY_VERDICTS = ['likely_generated', 'likely_authentic'] as const;

export const PhotoAdvisoryVerdictSchema = z.enum(PHOTO_ADVISORY_VERDICTS);

export type PhotoAdvisoryVerdict = (typeof PHOTO_ADVISORY_VERDICTS)[number];

/**
 * Why an advisory carries no verdict — the AI platform's own reasons plus the
 * two that are not its doing (the face-match shape, and for the same reason: a
 * coded set a staff surface renders in Arabic, never a sentence).
 */
export const PHOTO_ADVISORY_UNAVAILABLE_REASONS = [
  ...AI_UNAVAILABLE_REASONS,
  'evidence_missing',
  'evidence_unreadable',
] as const;

export const PhotoAdvisoryUnavailableReasonSchema = z.enum(PHOTO_ADVISORY_UNAVAILABLE_REASONS);

export type PhotoAdvisoryUnavailableReason = (typeof PHOTO_ADVISORY_UNAVAILABLE_REASONS)[number];

const REASON_SET: ReadonlySet<string> = new Set<string>(PHOTO_ADVISORY_UNAVAILABLE_REASONS);

/** Narrows a stored `photo_advisories.unavailable_reason`. */
export function isPhotoAdvisoryUnavailableReason(
  value: string,
): value is PhotoAdvisoryUnavailableReason {
  return REASON_SET.has(value);
}

/**
 * One advisory classification of a member's photograph as AI-generated or not
 * (`02-build.md` §P27).
 *
 * **Advisory means advisory.** Nothing reads this to decide anything: no step
 * moves, no status changes, no violation is recorded. It exists to inform the
 * human reviewing the case or the member, and it is on **staff** payloads only —
 * a member who can read the score can tune a render against it.
 *
 * `verdict`, `confidence`, `provider`, `model` and `reason` are null together:
 * when the check could not run, `unavailableReason` says why. `thresholdAtTime`
 * is always present — the snapshot of `verification.photoAdvisory.threshold` as
 * it stood when the check ran, so retuning the setting cannot re-judge a photo
 * already looked at. `confidence` is the model's score for *likely generated*;
 * the verdict is that score against the threshold.
 */
export const PhotoAdvisorySchema = z
  .object({
    mediaAssetId: z.uuid(),
    kind: PhotoAdvisoryKindSchema,
    verdict: PhotoAdvisoryVerdictSchema.nullable(),
    confidence: z.number().min(0).max(1).nullable(),
    thresholdAtTime: z.number().min(0).max(1),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    /** The classifier's rationale — what a reviewer checks the verdict against. */
    reason: z.string().max(4_000).nullable(),
    unavailableReason: PhotoAdvisoryUnavailableReasonSchema.nullable(),
    checkedAt: z.iso.datetime(),
  })
  .strict();

export type PhotoAdvisory = z.infer<typeof PhotoAdvisorySchema>;

/**
 * Whether the advisory flags the photo as generated — or `null` when it never
 * ran. `null` is a real third answer a surface must render: "not checked" is not
 * "authentic", and a badge that collapsed the two would clear a render because
 * a budget cap was reached.
 */
export function photoAdvisoryFlags(advisory: PhotoAdvisory): boolean | null {
  return advisory.verdict === null ? null : advisory.verdict === 'likely_generated';
}
